import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col, Tabs, Tab, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faCog, faHistory, faSync } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useUserContext } from '../layouts/UserContext';
import { getHatateToken } from '../helpers/utils';

type ImageAnalysisComponentProps = {
    language: 'es' | 'en';
    disabled?: boolean;
};

interface ImageHistoryItem {
    messageId: string;
    sender: string;
    timestamp: string;
    caption?: string;
    processed: boolean;
}

interface AnalysisConfig {
    enabled: boolean;
    autoReply: boolean;
    minConfidence: number;
    analyzeGroupImages: boolean;
    lastUpdated?: number;
}

const ImageAnalysisComponent: React.FC<ImageAnalysisComponentProps> = ({ language, disabled = false }) => {
    const {
        user
      } = useUserContext();
    // Estados para la configuración
    const [config, setConfig] = useState<AnalysisConfig>({
        enabled: false,
        autoReply: true,
        minConfidence: 0.7,
        analyzeGroupImages: false
    });
    
    // Estados para el historial de imágenes
    const [imageHistory, setImageHistory] = useState<ImageHistoryItem[]>([]);
    const [totalImages, setTotalImages] = useState(0);
    
    // Estados para subir/analizar imágenes
    const [uploadedImage, setUploadedImage] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState('');
    const [forwardResults, setForwardResults] = useState(false);
    const [contactId, setContactId] = useState('');
    
    // Estados generales
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // States para manejar auto-refresh del historial
    const [activeTab, setActiveTab] = useState('config');
    const [historyRefreshInterval, setHistoryRefreshInterval] = useState<NodeJS.Timeout | null>(null);
    
    // API info
    const API_KEY = 'anubis-whatsapp-api-key';
    const API_TOKEN_KEY = getHatateToken();
    const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/bot';
    const whatsAppServerPort = user.mlStuff.whatsAppServerPort;
    const whatsAppApiPort = user.mlStuff.whatsAppApiPort;
    const mlServerPort = user.mlStuff.mlServerPort;
    
 // Cargar configuración inicial
 useEffect(() => {
    // No más actualización global del historial
    loadConfiguration();
}, []);;

useEffect(() => {
    // Limpiar cualquier intervalo existente
    if (historyRefreshInterval) {
        clearInterval(historyRefreshInterval);
    }
    
    // Si estamos en la pestaña de historial, iniciar auto-refresh
    if (activeTab === 'history') {
        const interval = setInterval(() => {
            loadImageHistory();
        }, 30000);
        
        setHistoryRefreshInterval(interval);
        
        // Limpiar intervalo al desmontar o cambiar de pestaña
        return () => {
            clearInterval(interval);
        };
    }
}, [activeTab]);

    
    // Cargar la configuración desde la API
    const loadConfiguration = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await axios.get(
                `${API_BASE_URL}/image-analysis/config`,
                { 
                    headers: { 
                        'x-api-key': API_KEY,
                        'Authorization': `${API_TOKEN_KEY}`
                    },
                    mlStuff: {
                        whatsAppServerPort: whatsAppServerPort,
                        whatsAppApiPort: whatsAppApiPort,
                        mlServerPort: mlServerPort
                    }
                }
            );
            
            if (response.data.success && response.data.config) {
                setConfig(response.data.config);
            }
        } catch (err: any) {
            console.error('Error loading image analysis configuration:', err);
            setError(language === 'es'
                ? 'Error al cargar la configuración'
                : 'Error loading configuration');
        } finally {
            setIsLoading(false);
        }
    };
    
    // Guardar la configuración
    const saveConfiguration = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        
        try {
            const response = await axios.post(
                `${API_BASE_URL}/image-analysis/config`,
                config,
                { 
                    headers: { 
                        'x-api-key': API_KEY,
                        'Authorization': `${API_TOKEN_KEY}`
                    },
                    mlStuff: {
                        whatsAppServerPort: whatsAppServerPort,
                        whatsAppApiPort: whatsAppApiPort,
                        mlServerPort: mlServerPort
                  }
                }
            );
            
            if (response.data.success) {
                setSuccess(language === 'es'
                    ? 'Configuración guardada correctamente'
                    : 'Configuration saved successfully');
                
                // Ocultar mensaje de éxito después de 3 segundos
                setTimeout(() => {
                    setSuccess(null);
                }, 3000);
            } else {
                setError(response.data.message || (language === 'es'
                    ? 'Error al guardar la configuración'
                    : 'Error saving configuration'));
            }
        } catch (err: any) {
            console.error('Error saving configuration:', err);
            setError(language === 'es'
                ? 'Error al guardar la configuración'
                : 'Error saving configuration');
        } finally {
            setIsSaving(false);
        }
    };
    
    // Cargar historial de imágenes
    const loadImageHistory = async () => {
        setIsLoading(true);
        
        try {
            const response = await axios.get(
                `${API_BASE_URL}/image-history`,
                { 
                    headers: { 
                        'x-api-key': API_KEY,
                        'Authorization': `${API_TOKEN_KEY}`
                    },
                    mlStuff: {
                                    whatsAppServerPort: whatsAppServerPort,
                                    whatsAppApiPort: whatsAppApiPort,
                                    mlServerPort: mlServerPort
                                  },
                    params: { limit: 10 }
                }
            );
            
            if (response.data.success) {
                setImageHistory(response.data.images || []);
                setTotalImages(response.data.totalImages || 0);
            }
        } catch (err: any) {
            console.error('Error loading image history:', err);
            // No mostrar error para el historial, simplemente mostrar vacío
        } finally {
            setIsLoading(false);
        }
    };
    
// Analizar una imagen
const analyzeImage = async () => {
    if (!uploadedImage && !imageUrl) {
        setError(language === 'es'
            ? 'Debe proporcionar una imagen para analizar'
            : 'You must provide an image to analyze');
        return;
    }
    
    if (forwardResults && !contactId.trim()) {
        setError(language === 'es'
            ? 'Debe proporcionar un ID de contacto para reenviar los resultados'
            : 'You must provide a contact ID to forward the results');
        return;
    }
    
    setIsAnalyzing(true);
    setError(null);
    setSuccess(null);
    
    try {
        // Create a proper FormData object
        const formData = new FormData();
        
        // Only append one type of image source (file OR url, not both)
        if (uploadedImage) {
            // Make sure the field name 'image' matches what your server expects
            formData.append('image', uploadedImage);
            console.log('Añadido archivo de imagen:', uploadedImage.name);
        } else if (imageUrl) {
            formData.append('imageUrl', imageUrl);
            console.log('Añadida URL de imagen:', imageUrl);
        }
        
        // Add other necessary fields - IMPORTANTE: convertir boolean a string
        if (forwardResults) {
            formData.append('forwardResults', 'true'); // Enviar como string, no como boolean
            formData.append('chatId', contactId);
            formData.append('whatsappContact', contactId);
            console.log('Configurando reenvío a:', contactId);
        }
        
        console.log('Enviando formulario...');
        
        // Make the request with the correct content type (let browser set it)
        const response = await axios.post(
            `${API_BASE_URL}/analyze-image`,
            formData,
            { 
                headers: { 
                    'x-api-key': API_KEY,
                    'Authorization': `${API_TOKEN_KEY}`
                    // Don't manually set Content-Type, let axios set it with boundary
                }
            }
        );
            
            if (response.data.success) {
                setSuccess(language === 'es'
                    ? 'Imagen analizada correctamente'
                    : 'Image analyzed successfully');
                
                // Limpiar el formulario después de un éxito
                setUploadedImage(null);
                setImageUrl('');
                
                // Si se reenviaron los resultados, incluir esa info
                if (forwardResults && contactId) {
                    setSuccess(language === 'es'
                        ? `Imagen analizada correctamente. Resultados enviados a ${contactId}`
                        : `Image analyzed successfully. Results sent to ${contactId}`);
                }
                
                // Recargar el historial de imágenes
                loadImageHistory();
                
                // Ocultar mensaje de éxito después de 5 segundos
                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            } else {
                setError(response.data.message || (language === 'es'
                    ? 'Error al analizar la imagen'
                    : 'Error analyzing the image'));
            }
        } catch (err: any) {
            console.error('Error analyzing image:', err);
            setError(language === 'es'
                ? 'Error al analizar la imagen'
                : 'Error analyzing the image');
        } finally {
            setIsAnalyzing(false);
        }
    };
    
    // Manejar cambios en la imagen subida
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setUploadedImage(files[0]);
            setImageUrl(''); // Limpiar la URL si se sube un archivo
        }
    };
    
    // Formatear la fecha para mostrarla
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };
    
    // Acortar IDs para mostrarlos
    const shortenId = (id: string) => {
        if (!id) return '';
        if (id.includes('@')) {
            const parts = id.split('@');
            return parts[0].substring(0, 8) + '...@' + parts[1];
        }
        return id.substring(0, 12) + '...';
    };
    
    return (
        <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
                <FontAwesomeIcon icon={faImage} className="me-2" />
                {language === 'es' ? 'Análisis de Imágenes de WhatsApp' : 'WhatsApp Image Analysis'}
            </Card.Header>
            <Card.Body>
                <Tabs defaultActiveKey="config" activeKey={activeTab} onSelect={(eventKey) => setActiveTab(eventKey || 'config')} className="mb-3">
                    {/* Pestaña de Configuración */}
                    <Tab eventKey="config" title={
                        <span>
                            <FontAwesomeIcon icon={faCog} className="me-2" />
                            {language === 'es' ? 'Configuración' : 'Configuration'}
                        </span>
                    }>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                                {success}
                            </Alert>
                        )}
                        
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">{language === 'es' ? 'Cargando configuración...' : 'Loading configuration...'}</p>
                            </div>
                        ) : (
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Check 
                                        type="switch"
                                        id="image-analysis-enabled"
                                        label={language === 'es' ? 'Activar análisis de imágenes' : 'Enable image analysis'}
                                        checked={config.enabled}
                                        onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                                        disabled={disabled}
                                    />
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Check 
                                        type="switch"
                                        id="auto-reply-enabled"
                                        label={language === 'es' ? 'Respuesta automática con resultados' : 'Auto-reply with results'}
                                        checked={config.autoReply}
                                        onChange={(e) => setConfig({...config, autoReply: e.target.checked})}
                                        disabled={disabled || !config.enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        {language === 'es'
                                            ? 'Envía automáticamente los resultados del análisis como respuesta al mensaje'
                                            : 'Automatically sends analysis results as a reply to the message'}
                                    </Form.Text>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>{language === 'es' ? 'Confianza mínima' : 'Minimum confidence'}</Form.Label>
                                    <Form.Range
                                        min={0}
                                        max={100}
                                        step={5}
                                        value={config.minConfidence * 100}
                                        onChange={(e) => setConfig({...config, minConfidence: Number(e.target.value) / 100})}
                                        disabled={disabled || !config.enabled}
                                    />
                                    <div className="d-flex justify-content-between">
                                        <span>0%</span>
                                        <span className="fw-bold">{Math.round(config.minConfidence * 100)}%</span>
                                        <span>100%</span>
                                    </div>
                                    <Form.Text className="text-muted">
                                        {language === 'es'
                                            ? 'Nivel mínimo de confianza para incluir resultados en el análisis'
                                            : 'Minimum confidence level to include results in analysis'}
                                    </Form.Text>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Check 
                                        type="switch"
                                        id="group-images-enabled"
                                        label={language === 'es' ? 'Analizar imágenes de grupos' : 'Analyze group images'}
                                        checked={config.analyzeGroupImages}
                                        onChange={(e) => setConfig({...config, analyzeGroupImages: e.target.checked})}
                                        disabled={disabled || !config.enabled}
                                    />
                                    <Form.Text className="text-muted">
                                        {language === 'es'
                                            ? 'Procesar también las imágenes recibidas en chats grupales'
                                            : 'Also process images received in group chats'}
                                    </Form.Text>
                                </Form.Group>
                                
                                <Button 
                                    variant="primary" 
                                    onClick={saveConfiguration}
                                    disabled={disabled || isSaving}
                                >
                                    {isSaving ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            {language === 'es' ? 'Guardando...' : 'Saving...'}
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faCog} className="me-2" />
                                            {language === 'es' ? 'Guardar Configuración' : 'Save Configuration'}
                                        </>
                                    )}
                                </Button>
                            </Form>
                        )}
                    </Tab>
                    
                    {/* Pestaña de Análisis Manual */}
                    <Tab eventKey="analyze" title={
                        <span>
                            <FontAwesomeIcon icon={faImage} className="me-2" />
                            {language === 'es' ? 'Analizar Imagen' : 'Analyze Image'}
                        </span>
                    }>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                                {success}
                            </Alert>
                        )}
                        
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{language === 'es' ? 'Subir imagen' : 'Upload image'}</Form.Label>
                                        <Form.Control
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            disabled={disabled || isAnalyzing || Boolean(imageUrl)}
                                        />
                                        <Form.Text className="text-muted">
                                            {language === 'es'
                                                ? 'Selecciona una imagen de tu dispositivo'
                                                : 'Select an image from your device'}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{language === 'es' ? 'O URL de imagen' : 'Or image URL'}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="https://"
                                            value={imageUrl}
                                            onChange={(e) => {
                                                setImageUrl(e.target.value);
                                                setUploadedImage(null); // Limpiar el archivo si se usa URL
                                            }}
                                            disabled={disabled || isAnalyzing || Boolean(uploadedImage)}
                                        />
                                        <Form.Text className="text-muted">
                                            {language === 'es'
                                                ? 'Introduce la URL de una imagen en línea'
                                                : 'Enter the URL of an online image'}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Form.Group className="mb-3">
                                <Form.Check 
                                    type="switch"
                                    id="forward-results"
                                    label={language === 'es' ? 'Reenviar resultados a un contacto' : 'Forward results to a contact'}
                                    checked={forwardResults}
                                    onChange={(e) => setForwardResults(e.target.checked)}
                                    disabled={disabled || isAnalyzing}
                                />
                            </Form.Group>
                            
                            {forwardResults && (
                                <Form.Group className="mb-3">
                                    <Form.Label>{language === 'es' ? 'ID de contacto' : 'Contact ID'}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={language === 'es' 
                                            ? 'Ej: 5212345678901@s.whatsapp.net' 
                                            : 'E.g: 5212345678901@s.whatsapp.net'}
                                        value={contactId}
                                        onChange={(e) => setContactId(e.target.value)}
                                        disabled={disabled || isAnalyzing}
                                    />
                                    <Form.Text className="text-muted">
                                        {language === 'es'
                                            ? 'Introduce el ID completo del contacto incluyendo @s.whatsapp.net'
                                            : 'Enter the complete contact ID including @s.whatsapp.net'}
                                    </Form.Text>
                                </Form.Group>
                            )}
                            
                            <Button 
                                variant="success" 
                                onClick={analyzeImage}
                                disabled={disabled || isAnalyzing || (!uploadedImage && !imageUrl) || (forwardResults && !contactId.trim())}
                            >
                                {isAnalyzing ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {language === 'es' ? 'Analizando...' : 'Analyzing...'}
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faImage} className="me-2" />
                                        {language === 'es' ? 'Analizar Imagen' : 'Analyze Image'}
                                    </>
                                )}
                            </Button>
                        </Form>
                    </Tab>
                    
                    {/* Pestaña de Historial */}
                    <Tab eventKey="history" title={
                        <span>
                            <FontAwesomeIcon icon={faHistory} className="me-2" />
                            {language === 'es' ? 'Historial' : 'History'}
                            {totalImages > 0 && (
                                <Badge bg="info" className="ms-2">{totalImages}</Badge>
                            )}
                        </span>
                    }>
                        <div className="d-flex justify-content-between mb-3">
                            <h5>
                                {language === 'es' 
                                    ? `Imágenes procesadas (${totalImages} total)` 
                                    : `Processed images (${totalImages} total)`}
                            </h5>
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={loadImageHistory}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faSync} className={isLoading ? "fa-spin me-2" : "me-2"} />
                                {language === 'es' ? 'Actualizar' : 'Refresh'}
                            </Button>
                        </div>
                        
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">{language === 'es' ? 'Cargando historial...' : 'Loading history...'}</p>
                            </div>
                        ) : (
                            imageHistory.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>{language === 'es' ? 'Fecha' : 'Date'}</th>
                                                <th>{language === 'es' ? 'Remitente' : 'Sender'}</th>
                                                <th>{language === 'es' ? 'Caption' : 'Caption'}</th>
                                                <th>{language === 'es' ? 'Estado' : 'Status'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {imageHistory.map((item, index) => (
                                                <tr key={`${item.messageId}-${index}`}>
                                                    <td>{formatDate(item.timestamp)}</td>
                                                    <td>{shortenId(item.sender)}</td>
                                                    <td>{item.caption || '-'}</td>
                                                    <td>
                                                        {item.processed ? (
                                                            <Badge bg="success">
                                                                {language === 'es' ? 'Procesada' : 'Processed'}
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="warning">
                                                                {language === 'es' ? 'Pendiente' : 'Pending'}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <Alert variant="info">
                                    <FontAwesomeIcon icon={faImage} className="me-2" />
                                    {language === 'es' 
                                        ? 'No hay imágenes procesadas en el historial' 
                                        : 'No processed images in history'}
                                </Alert>
                            )
                        )}
                    </Tab>
                </Tabs>
            </Card.Body>
        </Card>
    );
};

export default ImageAnalysisComponent;